import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const TaglineBar = () => {
  return (
    <div className="bg-gray-50 border-b border-gray-200 py-2 text-center text-sm font-medium text-black">
      Your map to the world of AI
    </div>
  );
};

const Navigation = () => {
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 h-20">
      <div className="max-w-[1200px] mx-auto px-6 h-full flex items-center justify-between">
        <Link 
          to="/" 
          className="text-2xl font-bold text-black focus-visible-ring rounded"
          data-testid="logo-link"
        >
          AI Atlas
        </Link>
        
        <div className="hidden md:flex items-center gap-6">
          <Link 
            to="/" 
            className="text-base font-medium text-black hover:text-[#0044CC] transition-colors focus-visible-ring rounded px-2 py-1"
            data-testid="nav-home"
          >
            Home
          </Link>
          <Link 
            to="/prompt-helper" 
            className="text-base font-medium text-black hover:text-[#0044CC] transition-colors focus-visible-ring rounded px-2 py-1"
            data-testid="nav-prompt-helper"
          >
            Prompt Helper
          </Link>
          <Link 
            to="/transparency" 
            className="text-base font-medium text-black hover:text-[#0044CC] transition-colors focus-visible-ring rounded px-2 py-1"
            data-testid="nav-transparency"
          >
            Transparency
          </Link>
        </div>

        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-12 w-12 rounded-full"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white border-2 border-gray-100">
              <DropdownMenuItem onClick={() => navigate('/')} data-testid="mobile-nav-home">
                Home
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/prompt-helper')} data-testid="mobile-nav-prompt-helper">
                Prompt Helper
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/transparency')} data-testid="mobile-nav-transparency">
                Transparency
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gray-50 border-t border-gray-200 py-12 mt-20">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <h3 className="text-xl font-bold text-black mb-4">AI Atlas</h3>
            <p className="text-base text-[#333333] leading-relaxed">
              Your map to the world of AI. Clear guidance to help you choose the right AI tool.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-black mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-base text-[#333333] hover:text-[#0044CC] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/prompt-helper" className="text-base text-[#333333] hover:text-[#0044CC] transition-colors">
                  Prompt Helper
                </Link>
              </li>
              <li>
                <Link to="/transparency" className="text-base text-[#333333] hover:text-[#0044CC] transition-colors">
                  Transparency
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-base text-[#333333] hover:text-[#0044CC] transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-base text-[#333333] hover:text-[#0044CC] transition-colors">
                  Terms of Use
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-black mb-4">Contact</h4>
            <p className="text-base text-[#333333] leading-relaxed mb-3">
              Have questions or feedback?
            </p>
            <a 
              href="mailto:hello@youraiatlas.com" 
              className="text-base text-[#0044CC] hover:underline transition-colors"
            >
              hello@youraiatlas.com
            </a>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-sm text-[#333333]">
            © 2026 AI Atlas. Built with clarity and trust.
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <TaglineBar />
      <Navigation />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export { Layout, TaglineBar, Navigation, Footer };